import { MouseEventHandler, ReactElement, ReactNode, useCallback, WeakValidationMap } from 'react';
import PropTypes from 'prop-types';
import Button, { ButtonProps } from '../Button';
import CloseSmall from '@athena/forge-icons/dist/CloseSmall';
import Heading from '../Heading';
import StatusTag from '../StatusTag';
import { Theme } from '../Root/context';
import { forgeClassHelper } from '../utils/classes';
import Overlay, { OverlayProps } from '../Overlay';
import { isUndefined } from '../utils/helpers';
import { themeContextPropTypes, useTheme } from '../Root/ThemeContext';
import { DistributiveOmit } from '../utils/types';

const classes = forgeClassHelper({ name: 'lightbox', isPortal: true });

export type LightboxWidth = 'medium' | 'large' | 'full';
export type LightboxHeight = 'medium' | 'full' | 'auto';
export type LightboxButton = DistributiveOmit<ButtonProps<true> | ButtonProps<false>, 'autoFocus' | 'variant'>[];

export type LightboxFirstFocusType = 'headerButton' | 'closeButton' | undefined;

export interface LightboxNotDisableClose {
  disableClose?: false;
  onHide: () => void;
}

export interface LightboxDisableClose {
  disableClose: true;
  onHide?: () => void;
}

interface LeftPanelProps {
  children: ReactNode;
  height: LightboxHeight;
  headerText: string;
  leftIndicatorText?: string;
  padded: boolean;
}
interface RightPanelProps extends LightboxBaseProps {
  firstFocus: LightboxFirstFocusType;
  handleOnHide?: () => void;
}

export interface LightboxBaseProps extends OverlayProps {
  /** Screen reader label for the dialog. If none is provided, `headerText` will be used. */
  ariaLabel?: string;
  /** `Lightbox` content  */
  children?: ReactNode;
  /** Adds a class to the root element of the component */
  className?: string;
  /** Close button `aria-label` */
  closeButtonAriaLabel?: string;
  /** Removes the close button and prevents dialog closing when the backdrop is clicked */
  disableClose?: boolean;
  /** Array of secondary buttons to be added inline in the footer
   *
   * This type defaults useLink=false, because autoFocus doesn't exist for
   * anchor elements. */
  footerSecondaryButtons?: LightboxButton;
  /** Array of tertiary buttons to be added inline in the footer
   *
   * This type defaults useLink=false, because autoFocus doesn't exist for
   * anchor elements. */
  footerTertiaryButtons?: LightboxButton;
  /** Array of buttons to be added inline in the header
   *
   * This type defaults useLink=false, because autoFocus doesn't exist for
   * anchor elements. */
  headerButtons?: LightboxButton;
  /** Title text displayed at the top of the Lightbox. Will be used to label the dialog unless `aria-label` is provided. */
  headerText: string;
  /** Height of the LightBox. */
  height?: LightboxHeight;
  /** Hides borders that divide the header, body, and footer sections */
  hideDividers?: boolean;
  /** Title text displayed at the top of the Lightbox's left panel. */
  leftHeaderText?: string;
  /** leftIndicatorText text displayed at the top right of the Lightbox's left panel. */
  leftIndicatorText?: string;
  /** Contents of the left panel */
  leftPanelChildren?: ReactNode;
  /** Includes default button row */
  includeButtonRow?: boolean;
  /** Callback fired when the primary button is clicked */
  onPrimaryClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  /** Adds default padding to body that can be removed */
  padded?: boolean;
  /** Text for the primary action button */
  primaryButtonText?: string;
  /** Boolean to disable the primary button */
  primaryButtonDisabled?: boolean;
  /** Set the visibility of the Lightbox */
  show?: boolean;
  /** Status text displayed at the top of the Lightbox. */
  status?: string;
  /** Theme variables for configuring an instance of Forge. See Root component for more details. */
  theme?: Theme;
  /** Width of lightbox dialog box */
  width?: LightboxWidth;
}

export type LightboxProps = (LightboxDisableClose | LightboxNotDisableClose) & LightboxBaseProps;

const Lightbox = ({
  ariaLabel,
  children,
  className,
  closeButtonAriaLabel = 'Close Modal',
  disableClose = false,
  footerSecondaryButtons,
  footerTertiaryButtons,
  headerButtons,
  headerText,
  hideDividers = false,
  includeButtonRow = false,
  leftIndicatorText,
  leftHeaderText = '',
  leftPanelChildren,
  onHide,
  onPrimaryClick,
  padded = true,
  primaryButtonText = 'Next',
  primaryButtonDisabled = false,
  show = false,
  status,
  theme: upstreamTheme,
  height = leftPanelChildren ? 'medium' : 'auto',
  width = leftPanelChildren ? 'large' : 'medium',
  ...rest
}: LightboxProps): ReactElement => {
  const getFirstFocus = (): LightboxFirstFocusType => {
    if (headerButtons && headerButtons.length > 0) {
      return 'headerButton';
    } else if (!disableClose) {
      return 'closeButton';
    } else {
      return;
    }
  };

  const theme = useTheme(upstreamTheme);

  const firstFocus = getFirstFocus();

  const handleOnHide = useCallback((): void => {
    if (onHide && !disableClose) {
      // !disableClose is for vanilla js users
      onHide();
    }
  }, [onHide, disableClose]);

  const heightKey = `${height}_height`;
  const lightboxRightPanel = (
    <LightboxRightPanel
      closeButtonAriaLabel={closeButtonAriaLabel}
      disableClose={disableClose}
      firstFocus={firstFocus}
      footerSecondaryButtons={footerSecondaryButtons}
      footerTertiaryButtons={footerTertiaryButtons}
      height={height}
      handleOnHide={handleOnHide}
      headerButtons={headerButtons}
      headerText={headerText}
      includeButtonRow={includeButtonRow}
      onPrimaryClick={onPrimaryClick}
      padded={padded}
      primaryButtonText={primaryButtonText}
      primaryButtonDisabled={primaryButtonDisabled}
      status={status}
    >
      {children}
    </LightboxRightPanel>
  );

  return (
    <Overlay
      {...classes({ theme, extra: className })}
      dialogClass={classes({
        element: 'dialog',
        modifiers: {
          [width]: true,
          [heightKey]: true,
          'without-dividers': hideDividers,
        },
      })}
      onHide={handleOnHide}
      aria-label={ariaLabel || headerText}
      show={show}
      {...rest}
    >
      {leftPanelChildren && (
        <div {...classes({ element: 'row' })}>
          <div {...classes({ element: 'panels', modifiers: 'left' })}>
            <LightboxLeftPanel
              height={height}
              headerText={leftHeaderText}
              leftIndicatorText={leftIndicatorText}
              padded={padded}
            >
              {leftPanelChildren}
            </LightboxLeftPanel>
          </div>
          <div {...classes({ element: 'panels', modifiers: 'right' })}>{lightboxRightPanel}</div>
        </div>
      )}
      {!leftPanelChildren && lightboxRightPanel}
    </Overlay>
  );
};

const LightboxLeftPanel = ({ children, headerText, leftIndicatorText, padded }: LeftPanelProps): JSX.Element => {
  return (
    <>
      <div {...classes({ element: 'header-left' })}>
        <Heading text={headerText} headingTag={'h2'} variant={'subsection'} />
        {leftIndicatorText && <span>{leftIndicatorText}</span>}
      </div>
      <div
        {...classes({
          element: 'body',
          modifiers: { padded: padded },
        })}
      >
        {children}
      </div>
    </>
  );
};

const LightboxRightPanel = ({
  children,
  closeButtonAriaLabel,
  disableClose,
  firstFocus,
  footerSecondaryButtons,
  footerTertiaryButtons,
  handleOnHide,
  headerButtons,
  headerText,
  includeButtonRow,
  onPrimaryClick,
  padded = true,
  primaryButtonText,
  primaryButtonDisabled,
  status,
}: RightPanelProps): JSX.Element => {
  return (
    <>
      <div {...classes({ element: 'header' })}>
        {!status ? (
          <Heading text={headerText} headingTag={'h1'} variant={'section'} />
        ) : (
          <div {...classes({ element: 'header-text-with-tag' })}>
            <Heading text={headerText} headingTag={'h1'} variant={'section'} />
            <div>
              <StatusTag text={status} />
            </div>
          </div>
        )}
        {headerButtons &&
          headerButtons.map((button, i) => (
            <Button key={i} {...button} autoFocus={firstFocus === 'headerButton' && i === 0} variant="tertiary" />
          ))}
        {!disableClose && (
          <Button
            {...classes({ element: 'close' })}
            aria-label={closeButtonAriaLabel}
            onClick={handleOnHide}
            autoFocus={firstFocus === 'closeButton'}
            variant="tertiary"
            size="tiny"
            icon={CloseSmall}
          />
        )}
      </div>
      <div
        {...classes({
          element: 'body',
          modifiers: { padded },
        })}
      >
        {children}
      </div>
      {includeButtonRow && (
        <div {...classes({ element: 'footer' })}>
          {footerTertiaryButtons &&
            footerTertiaryButtons.map((button, i) => <Button key={'tertiary' + i} {...button} variant="tertiary" />)}
          {footerSecondaryButtons &&
            footerSecondaryButtons.map((button, i) => <Button key={'secondary' + i} {...button} variant="secondary" />)}
          <Button disabled={primaryButtonDisabled} text={primaryButtonText} onClick={onPrimaryClick} />
        </div>
      )}
    </>
  );
};

const lightboxPropTypes: WeakValidationMap<LightboxBaseProps & { '...rest': unknown }> = {
  /** Screen reader label for the dialog. If none is provided, `headerText` will be used. */
  ariaLabel: PropTypes.string,
  /** `Lightbox` content  */
  children: PropTypes.node,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Close button `aria-label` */
  closeButtonAriaLabel: PropTypes.string,
  /** Removes the close button and prevents dialog closing when the backdrop is clicked */
  disableClose: PropTypes.bool,
  /** Array of secondary buttons to be added inline in the footer
   *
   * This type defaults useLink=false, because autoFocus doesn't exist for
   * anchor elements. */
  footerSecondaryButtons: PropTypes.array,
  /** Array of tertiary buttons to be added inline in the footer
   *
   * This type defaults useLink=false, because autoFocus doesn't exist for
   * anchor elements. */
  footerTertiaryButtons: PropTypes.array,
  /** Array of buttons to be added inline in the header
   *
   * This type defaults useLink=false, because autoFocus doesn't exist for
   * anchor elements. */
  headerButtons: PropTypes.array,
  /** Title text displayed at the top of the Lightbox. Will be used to label the dialog unless `aria-label` is provided. */
  headerText: PropTypes.string.isRequired,
  /** Height of the LightBox. */
  height: PropTypes.oneOf(['medium', 'full', 'auto']),
  /** Hides borders that divide the header, body, and footer sections */
  hideDividers: PropTypes.bool,
  /** Title text displayed at the top of the Lightbox's left panel. */
  leftHeaderText: PropTypes.string,
  /** leftIndicatorText text displayed at the top right of the Lightbox's left panel. */
  leftIndicatorText: PropTypes.string,
  /** Contents of the left panel */
  leftPanelChildren: PropTypes.node,
  /** Includes default button row */
  includeButtonRow: PropTypes.bool,
  /**
   * Callback fired before the Lightbox transitions in. Used to manage other animations while is opening.\
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group)
   */
  onEnter: PropTypes.func,
  /**
   * Callback fired after the Lightbox finishes transitioning in. Used to manage other animations while is opening.\
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group)
   */
  onEntered: PropTypes.func,
  /**
   * Callback fired as the Lightbox begins to transition in. Used to manage other animations while is opening.\
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group)
   */
  onEntering: PropTypes.func,
  /**
   * Callback fired right before the Lightbox transitions out. Used to manage other animations while is closing.\
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group)
   */
  onExit: PropTypes.func,
  /**
   * Callback fired after the Lightbox finishes transitioning out. Used to manage other animations while is closing.\
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group)
   */
  onExited: PropTypes.func,
  /**
   * Callback fired as the Lightbox begins to transition out. Used to manage other animations while is closing.\
   * Generated by the underlying [React Transition Group](https://reactcommunity.org/react-transition-group)
   */
  onExiting: PropTypes.func,
  /**
   * Callback fired when the close button, backdrop, or secondary button is clicked, or the `ESC` key is pressed.
   *
   * The onHide callback only signals intent from the Lightbox, **you must actually set the** `show` **prop to** `false` **for the Lightbox to close.** Required unless `disableClose` is set.
   */
  onHide: function (props, propName, ...rest) {
    if (!props['disableClose'] && isUndefined(props[propName])) {
      return new Error('onHide is required to close lightbox');
    }
    return PropTypes.func(props, propName, ...rest);
  },
  /** Callback fired when the primary button is clicked */
  onPrimaryClick: PropTypes.func,
  /** A callback fired when the Lightbox is opening */
  onShow: PropTypes.func,
  /** Adds default padding to body that can be removed */
  padded: PropTypes.bool,
  /** Text for the primary action button */
  primaryButtonText: PropTypes.string,
  /** Boolean to disable the primary button */
  primaryButtonDisabled: PropTypes.bool,
  /** Set the visibility of the Lightbox */
  show: PropTypes.bool,
  /** Status text displayed at the top of the Lightbox. */
  status: PropTypes.string,
  /** Theme variables for configuring an instance of Forge. See Root component for more details. */
  theme: themeContextPropTypes,
  /** Width of lightbox dialog box */
  width: PropTypes.oneOf(['medium', 'large', 'full']),
  /** Passthrough props are passed through to the @restart/ui [Overlay](https://react-restart.github.io/ui/Overlay) component */
  '...rest': PropTypes.any,
};

Lightbox.propTypes = lightboxPropTypes;

export default Lightbox;
